"use client";
import React, { useEffect, useRef } from "react";
import { animate, useInView } from "framer-motion";
import { BlockUpdateFunction } from ".";
import { IBlocks, IConnections, ITheme, IUserData } from "@/types";
import AdminPopover from "../../content/AdminPopover";
import { Button } from "@/components/ui/button";
import BlockToggle from "./tabs/toggle";
import { Label } from "@/components/ui/label";
import Link from "next/link";
import moment from "moment";
import LoadingSkeleton from "./LoadingSkeleton";
import CountUp from "react-countup";
import { Separator } from "@/components/ui/separator";
import MediaKitOptions from "./tabs/mediakit/options";
import MediaKitCustomization from "./tabs/mediakit/customization";
import { getTextColorForBackground, shadow } from "@/lib/utils";
import {
    IconBrandTiktok,
    IconBrandTwitch,
    IconBrandTwitter,
    IconBrandYoutube,
    IconRosetteDiscountCheck,
} from "@tabler/icons-react";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { formatNumToK } from "@/lib/analyticValueFormats";
import CountUpComponent from "./Countup";
import { connect } from "http2";

interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    theme: ITheme;
    selectedPreviewDevice?: string;
    connections: {
        id: string;
        provider:
        | "twitch"
        | "youtube"
        | "twitter"
        | "bluesky"
        | "threads"
        | "instagram"
        | "tiktok"
        | "trovo";
        data: {
            [key: string]: any;
        };
    }[];
    newBlock?: string | null;
}

interface AnimatedStatProps {
    value: number;
}

export default function BlockMediaKit({
    admin,
    user,
    block,
    update,
    style,
    theme,
    selectedPreviewDevice,
    connections,
    newBlock,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Mediakit",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <MediaKitOptions
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        connections={connections}
                    />
                    <MediaKitCustomization
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        assets={[]}
                        user={user}
                    />
                </div>
            ),
        },
    });
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    const activeConnections: IConnections[] = blockUpdate.options.data
        ? blockUpdate.options.data
            .map((data: any) =>
                connections.find((connection) => connection.id === data.id)
            )
            .filter(Boolean)
        : [];
    const totalFollowers = activeConnections.reduce((total, connection) => {
        if (connection.provider === "twitch" && connection.data?.followers) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "youtube" &&
            connection.data?.subscriberCount
        ) {
            return total + parseInt(connection.data.subscriberCount);
        } else if (
            connection.provider === "twitter" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "tiktok" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "trovo" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        }
        return total;
    }, 0);
    // Calculate average updated_at timestamp
    const averageTimestamp =
        activeConnections.reduce((total, connection) => {
            return total + new Date(connection.updated_at).getTime();
        }, 0) / activeConnections.length;
    const averageDate = new Date(averageTimestamp);
    const formattedAverageDate = moment(averageDate).format("DD/MM/YY HH:mm");
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={`Media Kit |`}
            isOpen={isExpanded}
            onClose={handleClose}
        >
            <div
                id="mediakit"
                style={style}
                className={`{formatClassName(text)} 
                ${shadow(theme.button_shadow)}
                ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                flex 
                w-full 
                flex-col gap-1
                 px-2 
                py-3
                shadow-md
                transition-all
                `}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-lg font-bold">
                        {/* <p className="text-lg font-bold">Media Kit</p> */}
                    </div>
                    {blockUpdate.options?.total_followers && (
                        <div className="flex items-center gap-1 text-lg font-bold">
                            <p className="text-lg font-bold">
                                Total Followers:
                            </p>
                            <CountUpComponent
                                end={totalFollowers}
                                admin={admin}
                            />
                        </div>
                    )}
                </div>
                <div>
                    {activeConnections.map((connection, index: number) => {
                        let new_style: React.CSSProperties = {
                            background: style.background,
                            color: style.color,
                        };
                        const options = blockUpdate.options.data.find(
                            (e: any) => e.id == connection.id
                        );
                        if (connection.provider == "twitch") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-twitch-${connection.data.username}`}
                                        href={`https://twitch.tv/${connection.data.username}`}
                                        className={`{formatClassName(text)} flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            ${shadow(theme.button_shadow)}
                                            ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                                            `}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data.channel
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt="profile"
                                            />
                                            <IconBrandTwitch className="absolute bottom-0 right-0 rounded-tl-md bg-purple-500 p-0.5" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                <p className="text-xl font-semibold">
                                                    <CountUpComponent
                                                        end={
                                                            connection.data
                                                                .followers
                                                        }
                                                        admin={admin}
                                                    />{" "}
                                                    Followers
                                                </p>
                                                {options.subscribers && (
                                                    <p>
                                                        Subscribers:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .subscriptions
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {(options.average_viewers ||
                                                    options.recent_followers ||
                                                    options.hours_watched) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 30 Days
                                                            </p>
                                                            {options.average_viewers && (
                                                                <p>
                                                                    Average Viewers:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .avg_viewers
                                                                    }
                                                                </p>
                                                            )}
                                                            {options.recent_followers && (
                                                                <p>
                                                                    Followers
                                                                    Gained:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .followers
                                                                    }
                                                                </p>
                                                            )}
                                                            {options.hours_watched && (
                                                                <p>
                                                                    Hours Watched:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .hours_watched
                                                                    }
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "youtube") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-youtube-${connection.data.username}`}
                                        href={`https://youtube.com/${connection.data.username}`}
                                        className={`{formatClassName(text)} flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            ${shadow(theme.button_shadow)}
                                            ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                                            `}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data.channel
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt={`${connection.data.username} YouTube Channel`}
                                            />
                                            <IconBrandYoutube className="absolute bottom-0 right-0 rounded-tl-md bg-red-500 p-0.5" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                <p className="text-xl font-semibold">
                                                    <CountUpComponent
                                                        end={
                                                            connection.data
                                                                .subscriberCount
                                                        }
                                                        admin={admin}
                                                    />{" "}
                                                    Subscribers
                                                </p>
                                                {options.total_views && (
                                                    <span>
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .viewCount
                                                            }
                                                            admin={admin}
                                                        />{" "}
                                                        Views
                                                    </span>
                                                )}
                                                {(options.recent_views ||
                                                    options.viewer_approval ||
                                                    options.subscriber_change ||
                                                    options.average_watch_duration) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 30 Days
                                                            </p>
                                                            {options.recent_views && (
                                                                <p>
                                                                    Views:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.analytics
                                                                                ?.views ||
                                                                            0
                                                                        }
                                                                        admin={
                                                                            admin
                                                                        }
                                                                    />
                                                                </p>
                                                            )}
                                                            {options.viewer_approval && (
                                                                <p>
                                                                    Viewer Approval:{" "}
                                                                    {connection
                                                                        ?.data
                                                                        ?.analytics
                                                                        ?.likes &&
                                                                        connection?.data
                                                                            ?.analytics
                                                                            ?.dislikes
                                                                        ? (
                                                                            (connection
                                                                                .data
                                                                                .analytics
                                                                                .likes /
                                                                                (connection
                                                                                    .data
                                                                                    .analytics
                                                                                    .likes +
                                                                                    connection
                                                                                        .data
                                                                                        .analytics
                                                                                        .dislikes)) *
                                                                            100
                                                                        ).toFixed(
                                                                            1
                                                                        ) + "%"
                                                                        : "N/A"}
                                                                </p>
                                                            )}
                                                            {options.subscriber_change && (
                                                                <p>
                                                                    Subscriber
                                                                    Change:{" "}
                                                                    {(connection
                                                                        ?.data
                                                                        ?.analytics
                                                                        ?.subscribersGained ||
                                                                        0) -
                                                                        (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersLost ||
                                                                            0) >=
                                                                        0
                                                                        ? `+${(connection?.data?.analytics?.subscribersGained || 0) - (connection?.data?.analytics?.subscribersLost || 0)}`
                                                                        : (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersGained ||
                                                                            0) -
                                                                        (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersLost ||
                                                                            0)}
                                                                </p>
                                                            )}
                                                            {options.average_watch_duration && (
                                                                <p>
                                                                    Average Watch
                                                                    Duration:{" "}
                                                                    {Number(
                                                                        connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.averageViewPercentage ||
                                                                        0
                                                                    ).toLocaleString()}
                                                                    %
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "twitter") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-twitter-${connection.data.username}`}
                                        href={`https://twitter.com/${connection.data.username}`}
                                        className={`{formatClassName(text)} flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            ${shadow(theme.button_shadow)}
                                            ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                                            `}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                loading="lazy"
                                                alt="Profile Image"
                                            />
                                            <IconBrandTwitter className="absolute bottom-0 right-0 rounded-tl-md bg-[#3b82f6] p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={admin}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.following && (
                                                    <p>
                                                        Following:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .following
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {options.tweets && (
                                                    <p>
                                                        Tweets:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .tweets
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {options.likes && (
                                                    <p>
                                                        Likes:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .likes
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "tiktok") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-tiktok-${connection.data.username}`}
                                        href={`https://www.tiktok.com/@${connection.data.username}`}
                                        className={`{formatClassName(text)} flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            ${shadow(theme.button_shadow)}
                                            ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                                            `}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data?.avatar ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt={`${connection.data.username} avatar`}
                                            />
                                            <IconBrandTiktok className="absolute bottom-0 right-0 rounded-tl-md bg-[#130b18] p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={admin}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.following && (
                                                    <p>
                                                        Following:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .following
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {options.videos && (
                                                    <p>
                                                        Videos:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .videos
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {options.likes && (
                                                    <p>
                                                        Likes:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .likes
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                                {(options.recent_views ||
                                                    options.recent_likes) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 20 videos
                                                            </p>
                                                            {options.recent_views && (
                                                                <p>
                                                                    Views:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.statistics
                                                                                ?.views ||
                                                                            0
                                                                        }
                                                                        admin={
                                                                            admin
                                                                        }
                                                                    />
                                                                </p>
                                                            )}
                                                            {options.recent_likes && (
                                                                <p>
                                                                    Likes:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.statistics
                                                                                ?.likes ||
                                                                            0
                                                                        }
                                                                        admin={
                                                                            admin
                                                                        }
                                                                    />
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "trovo") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-trovo-${connection.data.username}`}
                                        href={`https://www.trovo.live/${connection.data.username}`}
                                        className={`{formatClassName(text)} flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            ${shadow(theme.button_shadow)}
                                            ${theme?.button_rounded == "rounded-full" ? "rounded-2xl" : theme?.button_rounded}
                                            `}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt="Profile Image"
                                            />
                                            <img
                                                src="https://cdn.doras.to/doras/icons/light/trovo.png"
                                                className="absolute bottom-0 right-0 h-[24px] w-[24px] rounded-tl-md bg-[#34cb81] p-0.5 text-white"
                                                alt="Trovo"
                                            />{" "}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={admin}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.subscribers && (
                                                    <p>
                                                        Subscribers:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .subscribers
                                                            }
                                                            admin={admin}
                                                        />
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        return;
                    })}
                </div>
                {/*  */}
                <Popover>
                    <PopoverTrigger asChild>
                        <div className="ml-auto flex cursor-pointer flex-col items-end gap-1">
                            <p className="flex items-center gap-1 text-sm">
                                <IconRosetteDiscountCheck size={18} /> Data
                                verified
                            </p>
                            <p className="flex items-center gap-1 text-xs">
                                Updated: {formattedAverageDate}
                            </p>
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        style={{
                            background: theme.accent_col || theme.button,
                            color: theme.accent_col
                                ? getTextColorForBackground(theme.accent_col)
                                : theme.text_col_button,
                        }}
                        className={`{formatClassName(text)} 
                ${shadow(theme.button_shadow)}
                ${theme?.button_rounded}
                flex 
                w-full 
                flex-col gap-3
                border-transparent px-2 
                py-3
                text-sm
                font-medium
                shadow-md
                `}
                    >
                        <p>
                            This data is verified by{" "}
                            <a
                                href={`https://doras.to${user.affiliate !== null ? "?aff=" + user.affiliate : ""}/`}
                                className="font-bold underline"
                            >
                                Doras.to
                            </a>
                            .
                        </p>
                    </PopoverContent>
                </Popover>
            </div>
        </AdminPopover>
    );
}
